import React, { useState, useRef } from 'react';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import Button from '@mui/material/Button';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import { CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Container from '../../GeneralComponents/Container';
import { Topbar, Sidebar } from '../components';
import setGlobalLoader from '../../../redux/actions/loaderActions';
import { createService } from '../../../redux/actions/apiActions';
import { s3Upload } from '../../../utils/uploadService';

export const SERVICE_TYPES = [
    { value: 'only_2_wheeler_license', label: 'Only 2 Wheeler License' },
    { value: 'only_4_wheeler_license', label: 'Only 4 Wheeler License' },
    { value: 'only_3_wheeler_license', label: 'Only 3 Wheeler License' },
    { value: '2_4_wheeler_license', label: '2 + 4 Wheeler License' },
    { value: '3_4_wheeler_license', label: '3 + 4 Wheeler License' },
    { value: '2_3_4_wheeler_license', label: '2 + 3 + 4 Wheeler License' },
    { value: 'transfer_of_ownership', label: 'Transfer of Ownership' },
    { value: 'hypothecation_termination', label: 'Hypothecation / Hire Purchase / Lease Termination' },
    { value: 'hypothecation_agreement', label: 'Hypothecation / Hire Purchase / Lease Agreement' },
    { value: 'new_vehicle_registration', label: 'New Vehicle Registration' },
    { value: 'heavy_transport_license', label: 'Heavy Transport License' },
    { value: 'issue_of_noc', label: 'Issue of NOC' },
    { value: 'cancellation_of_noc', label: 'Cancellation of NOC / CC' },
    { value: 'change_of_address_rc', label: 'Change of Address in RC' },
    { value: 'duplicate_rc', label: 'Duplicate RC' },
    { value: 'change_of_address_license', label: 'Change of Address in License' },
    { value: 'duplicate_license', label: 'Duplicate License' },
    { value: 'new_fitness_certificate', label: 'New Fitness Certificate' },
    { value: 'renewal_fitness_certificate', label: 'Renewal Fitness Certificate' },
];

export const PAYMENT_MODES = [
    { value: 'cash', label: 'Cash' },
    { value: 'online', label: 'Online (UPI / Card / Net Banking)' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
];

const validationSchema = yup.object({
    name: yup.string().trim().required('Customer name is required.'),
    mobileNumber: yup.string().trim().required('Mobile number is required.').matches(/^\d{10}$/, 'Mobile number must be exactly 10 digits.'),
    service: yup.string().required('Service type is required.'),
    amount: yup.number().required('Amount is required.').min(0, 'Amount must be positive.'),
    paymentMode: yup.string().required('Payment mode is required.'),
    description: yup.string().trim(),
    date: yup.string().required('Date is required.'),
});

const AddService = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });

    const [openSidebar, setOpenSidebar] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [newDocName, setNewDocName] = useState('');
    const [newDocFile, setNewDocFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const documentInputRef = useRef(null);

    const handleSidebarOpen = () => setOpenSidebar(true);
    const handleSidebarClose = () => setOpenSidebar(false);
    const open = isMd ? false : openSidebar;

    const handleDocFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
        if (!allowed.includes(file.type)) {
            toast.error('Only JPEG, PNG, WebP, or PDF files are allowed.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be under 10MB.');
            return;
        }
        setNewDocFile(file);
    };

    const handleAddDocument = () => {
        if (!newDocName.trim() || !newDocFile) {
            toast.error('Please enter a document name and select a file.');
            return;
        }
        setDocuments([...documents, { name: newDocName.trim(), file: newDocFile }]);
        setNewDocName('');
        setNewDocFile(null);
        if (documentInputRef.current) documentInputRef.current.value = '';
    };

    const handleRemoveDocument = (index) => {
        setDocuments(documents.filter((_, i) => i !== index));
    };

    const handleOnSubmit = async (values) => {
        dispatch(setGlobalLoader(true));
        setUploading(true);
        try {
            const payload = { ...values };

            if (documents.length > 0) {
                const uploadedDocs = [];
                for (const doc of documents) {
                    const result = await s3Upload(doc.file, { entity: 'service', entityId: 'new' });
                    if (result && result[0]?.url) {
                        uploadedDocs.push({ name: doc.name, url: result[0].url });
                    }
                }
                payload.documents = uploadedDocs;
            }

            await createService(payload);
            toast.success('Service recorded successfully');
            navigate('/services');
        } catch (error) {
            console.log('create service error', error);
            toast.error(error?.response?.data?.message ?? 'Error recording service');
        } finally {
            setUploading(false);
            dispatch(setGlobalLoader(false));
        }
    };

    return (
        <Box>
            <AppBar
                position={'fixed'}
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
                elevation={0}
            >
                <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                    <Topbar onSidebarOpen={handleSidebarOpen} />
                </Container>
            </AppBar>
            <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? 'permanent' : 'temporary'} />
            <main>
                <Box height={{ xs: 94, sm: 102, md: 107 }} />
                <br />
                <br />
                <Box overflow="hidden" marginLeft={{ md: '270px' }} paddingRight={{ xs: 2, md: 4 }}>
                    <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                        Add Service
                    </Typography>

                    <Formik
                        initialValues={{
                            name: '',
                            mobileNumber: '',
                            service: '',
                            amount: '',
                            paymentMode: '',
                            description: '',
                            date: new Date().toISOString().split('T')[0],
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values) => handleOnSubmit(values)}
                        enableReinitialize
                    >
                        {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={{ xs: 2, md: 4 }} sx={{ padding: '10px' }}>
                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Customer Name *
                                        </Typography>
                                        <TextField
                                            label="Customer Name"
                                            variant="outlined"
                                            name="name"
                                            fullWidth
                                            value={values.name}
                                            onChange={(e) => setFieldValue('name', e.target.value)}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Mobile Number *
                                        </Typography>
                                        <TextField
                                            label="Mobile Number"
                                            variant="outlined"
                                            name="mobileNumber"
                                            fullWidth
                                            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                            value={values.mobileNumber}
                                            onChange={(e) => setFieldValue('mobileNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                                            error={touched.mobileNumber && Boolean(errors.mobileNumber)}
                                            helperText={touched.mobileNumber && errors.mobileNumber}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Service Type *
                                        </Typography>
                                        <Select
                                            styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                                            placeholder="Select Service Type"
                                            value={SERVICE_TYPES.find((o) => o.value === values.service) || null}
                                            options={SERVICE_TYPES}
                                            onChange={(e) => setFieldValue('service', e.value)}
                                        />
                                        {touched.service && errors.service && (
                                            <Typography variant="caption" color="error">{errors.service}</Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Amount *
                                        </Typography>
                                        <TextField
                                            label="Amount (₹)"
                                            variant="outlined"
                                            name="amount"
                                            type="number"
                                            fullWidth
                                            InputProps={{ inputProps: { min: 0 } }}
                                            value={values.amount}
                                            onChange={(e) => setFieldValue('amount', e.target.value)}
                                            error={touched.amount && Boolean(errors.amount)}
                                            helperText={touched.amount && errors.amount}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Payment Mode *
                                        </Typography>
                                        <Select
                                            styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                                            placeholder="Select Payment Mode"
                                            value={PAYMENT_MODES.find((o) => o.value === values.paymentMode) || null}
                                            options={PAYMENT_MODES}
                                            onChange={(e) => setFieldValue('paymentMode', e.value)}
                                        />
                                        {touched.paymentMode && errors.paymentMode && (
                                            <Typography variant="caption" color="error">{errors.paymentMode}</Typography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Date *
                                        </Typography>
                                        <TextField
                                            label="Date"
                                            variant="outlined"
                                            name="date"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={values.date}
                                            onChange={(e) => setFieldValue('date', e.target.value)}
                                            error={touched.date && Boolean(errors.date)}
                                            helperText={touched.date && errors.date}
                                        />
                                    </Grid>


                                    <Grid item xs={12} sm={6} md={7}>
                                        <Typography variant={'subtitle2'} sx={{ marginBottom: 2 }} fontWeight={700}>
                                            Description
                                        </Typography>
                                        <TextField
                                            label="Description"
                                            variant="outlined"
                                            name="description"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={values.description}
                                            onChange={(e) => setFieldValue('description', e.target.value)}
                                            error={touched.description && Boolean(errors.description)}
                                            helperText={touched.description && errors.description}
                                        />
                                    </Grid>

                                    {/* Documents */}
                                    <Grid item xs={12}>
                                        <Typography variant={'subtitle2'} sx={{ mb: 2 }} fontWeight={700}>
                                            Documents
                                        </Typography>
                                        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
                                            <TextField
                                                placeholder="Document name (e.g. Receipt)"
                                                variant="outlined"
                                                size="small"
                                                value={newDocName}
                                                onChange={(e) => setNewDocName(e.target.value)}
                                                sx={{ minWidth: 200 }}
                                            />
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <input
                                                    type="file"
                                                    accept="image/*,application/pdf"
                                                    hidden
                                                    ref={documentInputRef}
                                                    onChange={handleDocFileSelect}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<CloudUploadIcon />}
                                                    onClick={() => documentInputRef.current?.click()}
                                                >
                                                    {newDocFile ? newDocFile.name : 'Select File'}
                                                </Button>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddDocument}
                                                disabled={!newDocName.trim() || !newDocFile}
                                            >
                                                Add
                                            </Button>
                                        </Box>

                                        {documents.length > 0 && (
                                            <Box mt={2}>
                                                {documents.map((doc, index) => (
                                                    <Box
                                                        key={index}
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={2}
                                                        mb={1}
                                                        p={1}
                                                        sx={{ bgcolor: 'grey.100', borderRadius: 1 }}
                                                    >
                                                        <InsertDriveFileIcon fontSize="small" color="action" />
                                                        <Typography variant="body2" fontWeight={600} flex={1}>
                                                            {doc.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {doc.file.name}
                                                        </Typography>
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveDocument(index)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>

                                <br />
                                <Button
                                    component={'button'}
                                    variant={'contained'}
                                    type="submit"
                                    disabled={isSubmitting || uploading}
                                >
                                    {isSubmitting || uploading ? <CircularProgress size={24} /> : 'Submit'}
                                </Button>
                            </form>
                        )}
                    </Formik>
                </Box>
            </main>
        </Box>
    );
};

export default AddService;
